import { faCube } from "@fortawesome/pro-regular-svg-icons";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { Card } from "src/base-components/Card";
import { Select } from "src/base-components/Select";
import {
  getOrderedProperties,
  getSchemaIcon,
} from "src/entities/entityView/utils";
import { EntitySchemaResource } from "src/entities/queries";
import { CustomTitle } from "src/entityActionNodes/LocateEntityCard";
import { WriteEntityNodeForm } from "src/entityActionNodes/WriteEntityNodeEditor";
import { getTypeIcon } from "src/utils/icons";

export const ID_KEY = "id";

export const EntityPropertiesCard = ({
  immutable,
  entitySchemas,
}: {
  immutable: boolean;
  entitySchemas: EntitySchemaResource[];
}) => {
  const formMethods = useFormContext<WriteEntityNodeForm>();
  const entitySchema = formMethods.watch("entity_schema");
  const selectedSchema = useMemo(() => {
    return entitySchemas.find((schema) => schema._id === entitySchema);
  }, [entitySchemas, entitySchema]);

  const options = useMemo(() => {
    if (!selectedSchema) return [];
    const properties = getOrderedProperties(selectedSchema);
    const propertyOptions = properties.map((property) => {
      const propertyData = selectedSchema.properties[property];
      const schemaFinder = (schema: EntitySchemaResource) =>
        schema._id === propertyData._rel_schema;
      const icon = (() => {
        if (propertyData._type === "relation") {
          const relSchema = entitySchemas.find(schemaFinder);
          return relSchema ? getSchemaIcon(relSchema) : faCube;
        }
        return getTypeIcon(propertyData._type);
      })();
      return {
        key: property,
        value: selectedSchema.properties[property]._display_name,
        icon,
        disabled: property === ID_KEY,
      };
    });

    return propertyOptions;
  }, [selectedSchema, entitySchemas]);

  const { field: propertiesToWriteField } = useController({
    control: formMethods.control,
    name: "properties_to_write",
  });

  return (
    <Card>
      <Card.Header>
        <CustomTitle
          description="On update, only the selected properties are changed; everything else stays as is."
          title="Select Entity properties to create or update"
          required
        />
      </Card.Header>
      <Card.Content>
        <Select
          dataLoc="entity-properties-selector"
          disabled={immutable}
          options={options}
          selectAllOption={{
            value: "Add all",
          }}
          value={propertiesToWriteField.value}
          multiple
          showSelectAllAsOption
          onChange={(values) => {
            if (values.length === 0) {
              propertiesToWriteField.onChange([ID_KEY]);
            } else {
              propertiesToWriteField.onChange(values);
            }
          }}
        />
      </Card.Content>
    </Card>
  );
};
